import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, OnChanges,
  Output, SimpleChanges, ViewChild
} from "@angular/core";
import { DecisionAction, DecisionDetail } from "../../../models/home";
import { UxError } from "../../../models/ux-error.model";
import { HomeDecisionActionUxService, DecisionActionPresentation } from "../../../services/home-ia/home-decision-action-ux.service";
import {
  DecisionConfidenceDisplay, DecisionMissingInformationDisplay, DecisionPriorityDisplay,
  DecisionRiskDisplay, DecisionTechnicalEntry, HomeDecisionDisplayUxService
} from "../../../services/home-ia/home-decision-display-ux.service";
import { HomeDecisionDetailI18nService } from "../../../services/home-ia/home-decision-detail-i18n.service";
import { LanguageService } from "../../../services/language/language.service";
import { formatDecisionTitle } from "../../../utils/decision-title-format.util";
import { HomeDecisionActionFormComponent } from "../home-decision-action-form/home-decision-action-form.component";
import { HomeDecisionDetailActionDispatcher } from "./home-decision-detail-action.dispatcher";
import { HomeDecisionDetailViewContext, HomeDecisionDetailViewModelBuilder } from "./home-decision-detail-view-model.builder";
import { HomeDecisionDetailInspectionCoordinator, HomeDecisionDetailSectionState } from "./home-decision-detail-inspection.coordinator";
import {
  DecisionActionRequestEvent, DecisionActivityDisplay, DecisionTechnicalDisplay,
  DetailSectionKey, InspectionTarget
} from "./home-decision-detail.types";

@Component({
  selector: "app-home-decision-detail",
  templateUrl: "./home-decision-detail.component.html",
  styleUrls: [
    "./home-decision-detail.component.shell-and-summary.scss",
    "./home-decision-detail.component.timeline-and-actions.scss",
    "./home-decision-detail.component.missing-data-and-responsive.scss"
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeDecisionDetailComponent implements OnChanges {
  @Input() detail: DecisionDetail | null = null;
  @Input() loading = false;
  @Input() error: UxError | null = null;
  @Input() actionLoading = false;
  @Input() actionMessage: string | null = null;
  @Input() actionError: UxError | null = null;
  @Output() retryRequest = new EventEmitter<void>();
  @Output() closeRequest = new EventEmitter<void>();
  @Output() executeActionRequest = new EventEmitter<DecisionActionRequestEvent>();
  @ViewChild("evidenceSection") evidenceSection?: ElementRef<HTMLElement>;
  @ViewChild("missingInformationSection") missingInformationSection?: ElementRef<HTMLElement>;
  @ViewChild("sourceTraceSection") sourceTraceSection?: ElementRef<HTMLElement>;
  @ViewChild("actionSection") actionSection?: ElementRef<HTMLElement>;
  @ViewChild("feedbackRegion") feedbackRegion?: ElementRef<HTMLElement>;
  @ViewChild(HomeDecisionActionFormComponent) actionForm?: HomeDecisionActionFormComponent;
  readonly formatDecisionTitle = formatDecisionTitle;

  private readonly sectionState = new HomeDecisionDetailSectionState();
  private readonly inspection = new HomeDecisionDetailInspectionCoordinator(this.sectionState);
  private readonly viewModels: HomeDecisionDetailViewModelBuilder;
  private readonly actionDispatcher: HomeDecisionDetailActionDispatcher;

  constructor(
    public readonly lang: LanguageService,
    @Inject(HomeDecisionDetailI18nService) public readonly detailI18n: HomeDecisionDetailI18nService,
    private readonly actionUx: HomeDecisionActionUxService,
    decisionDisplayUx: HomeDecisionDisplayUxService
  ) {
    this.viewModels = new HomeDecisionDetailViewModelBuilder(lang, detailI18n, actionUx, decisionDisplayUx);
    this.actionDispatcher = new HomeDecisionDetailActionDispatcher(actionUx);
  }

  get technicalTraceExpanded(): boolean { return this.sectionState.technicalTraceExpanded; }
  set technicalTraceExpanded(value: boolean) { this.sectionState.technicalTraceExpanded = value; }
  get highlightedSection(): InspectionTarget { return this.sectionState.highlightedSection; }
  set highlightedSection(value: InspectionTarget) { this.sectionState.highlightedSection = value; }
  get selectedActionKey(): string | null { return this.sectionState.selectedActionKey; }
  set selectedActionKey(value: string | null) { this.sectionState.selectedActionKey = value; }
  private get expandedSections(): Record<DetailSectionKey, boolean> { return this.sectionState.expandedSections; }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["detail"]) {
      this.syncSelectedAction();
      this.resetSectionExpansion();
      this.highlightedSection = null;
    }
    if ((changes["actionMessage"] || changes["actionError"]) && (this.actionMessage || this.actionError)) {
      queueMicrotask(() => this.feedbackRegion?.nativeElement.focus({ preventScroll: true }));
    }
  }

  private get context(): HomeDecisionDetailViewContext {
    return {
      detail: this.detail, selectedActionKey: this.selectedActionKey, actionLoading: this.actionLoading,
      technicalTraceExpanded: this.technicalTraceExpanded,
      inspectionTarget: this.resolveInspectionTarget(this.detail?.decision ?? null)
    };
  }

  focusInspectionTarget(): void {
    if (!this.detail?.decision) return;
    this.inspection.focusTarget(this.resolveInspectionTarget(this.detail.decision), {
      action: this.actionSection?.nativeElement, evidence: this.evidenceSection?.nativeElement,
      missing: this.missingInformationSection?.nativeElement, technical: this.sourceTraceSection?.nativeElement
    });
  }
  resolveInspectionTarget(decision: DecisionDetail["decision"] | null): InspectionTarget {
    return this.inspection.resolveTarget(decision, this.hasSourceTrace);
  }
  syncSelectedAction(): void { this.sectionState.syncSelectedAction(this.availableActions); }
  expandSection(section: DetailSectionKey): void { this.sectionState.expand(section); }
  resetSectionExpansion(): void { this.sectionState.reset(); }

  get actionPresentation(): DecisionActionPresentation { return this.viewModels.actionPresentation(this.context); }
  get actionDisabledReason(): string | null { return this.viewModels.actionDisabledReason(this.context); }
  get showPrimaryAction(): boolean { return this.viewModels.showPrimaryAction(this.context); }
  get detailPrimaryActionLabel(): string | null { return this.viewModels.primaryActionLabel(this.context); }
  get inspectActionHint(): string | null { return this.viewModels.inspectActionHint(this.context); }
  get isPrimaryActionDisabled(): boolean { return this.viewModels.isPrimaryActionDisabled(this.context); }
  get availableActions(): readonly DecisionAction[] { return this.viewModels.availableActions(this.detail); }
  get selectedAction(): DecisionAction | null { return this.viewModels.selectedAction(this.context); }
  get secondaryActions(): readonly DecisionAction[] { return this.viewModels.secondaryActions(this.detail); }
  get remediationActions(): readonly DecisionAction[] { return this.viewModels.remediationActions(this.detail); }
  get dangerActions(): readonly DecisionAction[] { return this.viewModels.dangerActions(this.detail); }
  get disabledActions(): readonly DecisionAction[] { return this.viewModels.disabledActions(this.detail); }
  get showGroupedActionCatalog(): boolean { return this.availableActions.length <= 1; }
  get selectedActionNeedsForm(): boolean { return this.viewModels.selectedActionNeedsForm(this.context); }
  get canRenderSelectedActionForm(): boolean { return !!this.selectedAction?.inputSchema; }
  get selectedActionUnavailableReason(): string | null { return this.viewModels.selectedActionUnavailableReason(this.context); }
  get hasSourceTrace(): boolean { return this.technicalEntries.length > 0; }
  get technicalEntries(): readonly DecisionTechnicalEntry[] { return this.viewModels.technicalEntries(this.detail); }
  get technicalEntryDisplays(): readonly DecisionTechnicalDisplay[] { return this.viewModels.technicalEntryDisplays(this.detail); }
  get priorityDisplay(): DecisionPriorityDisplay { return this.viewModels.priorityDisplay(this.detail); }
  get riskDisplay(): DecisionRiskDisplay { return this.viewModels.riskDisplay(this.detail); }
  get confidenceDisplay(): DecisionConfidenceDisplay { return this.viewModels.confidenceDisplay(this.detail); }
  get missingInformationDisplays(): readonly DecisionMissingInformationDisplay[] { return this.viewModels.missingInformationDisplays(this.detail); }
  get translatedDecisionReason(): string | null { return this.viewModels.translatedDecisionReason(this.detail); }
  get translatedActionSummaryLabel(): string { return this.viewModels.translatedActionSummaryLabel(this.context); }
  get translatedActionSummaryCopy(): string { return this.viewModels.translatedActionSummaryCopy(this.context); }
  get translatedCompletionCriteria(): readonly string[] { return this.viewModels.translatedCompletionCriteria(this.context); }
  get translatedSelectedActionDescription(): string { return this.viewModels.translatedSelectedActionDescription(this.context); }
  get historyPreviewDisplays(): readonly DecisionActivityDisplay[] { return this.viewModels.historyPreviewDisplays(this.detail); }
  get technicalTraceToggleLabel(): string { return this.viewModels.technicalTraceToggleLabel(this.technicalTraceExpanded); }

  isSectionExpanded(section: DetailSectionKey | "technical"): boolean { return this.sectionState.isExpanded(section); }
  onRetry(): void { this.retryRequest.emit(); }
  onClose(): void { this.closeRequest.emit(); }
  onSelectAction(actionKey: string): void {
    this.selectedActionKey = actionKey;
    if (this.selectedActionNeedsForm) queueMicrotask(() => this.actionForm?.focusFirstField());
  }
  onPrimaryAction(): void {
    if (this.actionPresentation.primaryMode === "inspect") return this.focusInspectionTarget();
    if (this.actionPresentation.primaryMode !== "execute") return;
    const request = this.actionDispatcher.buildPrimaryRequest(this.detail, this.actionPresentation.action as DecisionAction | null);
    if (request) this.executeActionRequest.emit(request);
  }
  onActionFormSubmit(formValue: Record<string, unknown>): void {
    const request = this.actionDispatcher.buildFormRequest(this.detail, this.selectedAction, formValue);
    if (request) this.executeActionRequest.emit(request);
  }
  toggleTechnicalTrace(): void { this.sectionState.toggle("technical"); }
  toggleSection(section: DetailSectionKey | "technical"): void { this.sectionState.toggle(section); }
  trackByActionKey(_: number, action: DecisionAction): string { return action.actionKey; }
  translateActionLabel(action: DecisionAction): string { return this.detailI18n.translateActionLabel(action) ?? action.label; }
  translateActionDisabledCopy(reason?: string | null): string | null { return this.detailI18n.translateActionDisabledReason(reason); }
  translateEvidenceLabel(evidence: NonNullable<DecisionDetail["decision"]>["evidence"][number]): string { return this.detailI18n.translateEvidenceLabel(evidence); }
  translateEvidenceValue(evidence: NonNullable<DecisionDetail["decision"]>["evidence"][number]): string { return this.detailI18n.translateEvidenceValue(evidence); }
}
